// What a custom widget reports back once it has drawn, shared by the browser
// that draws it and the server that tells the agent who wrote it. An agent
// cannot see the board, so this is how it learns that its widget threw an
// error, drew nothing, showed "NaN" or ran off the bottom of its tile.

export interface RenderReport {
  w: number; // the frame's size, in CSS pixels
  h: number;
  sw: number; // the content's scroll size
  sh: number;
  scrolls: boolean; // the widget scrolls on purpose
  used: number; // how far down the tile the content reaches, 0 to 1
  text: string; // what it says, squashed to one line
  blank: boolean; // no text and no drawing
  odd: string | null; // "NaN", "undefined"... a value that went missing
  errors: { message: string; line: number | null }[];
}

/**
 * Every web link in a widget's own data: an item's href, a row's or the json's
 * links. A custom widget may open these and nothing else, so its code cannot
 * make up an address to carry what it shows out of its sealed frame.
 */
export function linksIn(s: { items?: { href?: string | null }[] | null; rows?: Record<string, unknown>[] | null; json?: unknown } | null | undefined): Set<string> {
  const out = new Set<string>();
  const add = (v: unknown) => {
    if (typeof v !== "string" || !/^https?:\/\//i.test(v)) return;
    const h = webLink(v);
    if (h) out.add(h);
  };
  if (!s) return out;
  for (const it of s.items ?? []) add(it?.href);
  for (const r of s.rows ?? []) if (r && typeof r === "object") Object.values(r).forEach(add);
  // a json answer's links, as deep and as many as a sensible answer has
  const walk = (v: unknown, depth: number) => {
    if (out.size >= 2000 || depth > 6) return;
    if (typeof v === "string") add(v);
    else if (Array.isArray(v)) v.slice(0, 500).forEach((x) => walk(x, depth + 1));
    else if (v && typeof v === "object") Object.values(v).slice(0, 200).forEach((x) => walk(x, depth + 1));
  };
  walk(s.json, 0);
  return out;
}

/** An http or https address, written the one way a browser writes it, or null. */
export function webLink(v: string): string | null {
  try {
    const u = new URL(v);
    return u.protocol === "https:" || u.protocol === "http:" ? u.toString() : null;
  } catch {
    return null;
  }
}

/** A short fingerprint of a widget's html, so a report is matched to the version it is about. */
export function htmlHash(html: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < html.length; i++) {
    h ^= html.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36) + html.length.toString(36);
}

export function cleanReport(raw: unknown): RenderReport | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const n = (x: unknown) => (typeof x === "number" && Number.isFinite(x) ? Math.max(0, Math.min(100_000, Math.round(x))) : 0);
  const errors = Array.isArray(r.errors)
    ? r.errors
        .slice(0, 3)
        .map((e) => ({ message: String((e as { message?: unknown })?.message ?? "").slice(0, 300), line: typeof (e as { line?: unknown })?.line === "number" ? Math.round((e as { line: number }).line) : null }))
        .filter((e) => e.message)
    : [];
  return {
    w: n(r.w),
    h: n(r.h),
    sw: n(r.sw),
    sh: n(r.sh),
    scrolls: r.scrolls === true,
    used: typeof r.used === "number" && Number.isFinite(r.used) ? Math.max(0, Math.min(1, r.used)) : 1,
    text: typeof r.text === "string" ? r.text.slice(0, 280) : "",
    blank: r.blank === true,
    odd: typeof r.odd === "string" ? r.odd.slice(0, 20) : null,
    errors,
  };
}

/** What went wrong, if anything, in the words the tile and the agent both use. */
export function problems(r: RenderReport): string[] {
  const out: string[] = [];
  for (const e of r.errors) out.push(`It threw an error${e.line ? ` at line ${e.line}` : ""}: ${e.message}`);
  if (r.blank && !r.errors.length) out.push("It drew nothing you can see.");
  if (r.odd) out.push(`It shows “${r.odd}” where a value should be.`);
  if (!r.scrolls && r.sh > r.h + 6) out.push(`Its content is ${r.sh}px tall but the tile shows ${r.h}px, so the bottom is cut off.`);
  if (!r.scrolls && r.sw > r.w + 8) out.push(`Its content is ${r.sw}px wide but the tile shows ${r.w}px, so the side is cut off.`);
  return out;
}

/**
 * The note an agent gets after adding or changing a custom widget. "phone":
 * every window with the board open shows its narrow layout, the one a window
 * under 720px wide gets, a phone's or a narrow desktop window's alike.
 */
export function renderNote(r: RenderReport | "nobody" | "elsewhere" | "phone" | null): string {
  if (r === "nobody") return "Nobody has the board open right now, so it has not drawn yet. It draws when they open it; read_board then shows how it went.";
  if (r === "elsewhere") return "The user is looking at a different board, so it has not drawn yet. read_board shows how it went once it has.";
  if (r === "phone") return "The board is open only in its narrow, phone-sized layout, which shows widgets one under another at another size, so how it draws in its tile on the board is not known yet. read_board shows it once the board is open in a wider window.";
  if (!r) return "It had not finished drawing after a few seconds. Call read_board in a moment to see how it went.";
  const issues = problems(r);
  const shows = r.text ? ` It shows: “${r.text.length > 200 ? r.text.slice(0, 199) + "…" : r.text}”` : "";
  if (!issues.length) {
    const roomy = r.used < 0.45 && r.h > 160 ? ` Its content only reaches ${Math.round(r.used * 100)}% of the way down the tile: let it fill the space, or use size small.` : "";
    return `It drew cleanly in the user's browser at ${r.w}×${r.h}.${shows}${roomy}`;
  }
  return `It drew at ${r.w}×${r.h} in the user's browser, with problems:\n${issues.map((x) => `- ${x}`).join("\n")}\nFix it with update_widget.${shows}`;
}
